import { useState, useMemo, useEffect } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { PeriodFilter, Period } from "@/components/PeriodFilter";
import { ProductSelector } from "@/components/ProductSelector";
import { MetricCard } from "@/components/MetricCard";
import { RevenueChart } from "@/components/RevenueChart";
import { ChurnChart } from "@/components/ChurnChart";
import { useMetrics } from "@/hooks/useMetrics";
import { useMetricsTimeSeries } from "@/hooks/useMetricsTimeSeries";
import { useProduct } from "@/contexts/ProductContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { subDays, format } from "date-fns";
import {
  DollarSign,
  TrendingUp,
  Users,
  Target,
  ShoppingCart,
  Calendar,
} from "lucide-react";

const formatDateRange = (startDate: Date, endDate: Date) => {
  return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`;
};

const Index = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>("30d");
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();
  const { selectedProduct } = useProduct();
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Dashi | Dashboard';
  }, []);

  // Calcular datas baseado no período selecionado
  const dateRange = useMemo(() => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    let startDate: Date;
    let endDate: Date;

    switch (selectedPeriod) {
      case "7d":
        // Últimos 7 dias: De (ontem - 6 dias) até ontem
        endDate = yesterday;
        startDate = subDays(yesterday, 6);
        break;
      case "30d":
        // Mês Atual: Do dia 1º do mês vigente até ontem
        endDate = yesterday;
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "90d":
        // Último Trimestre: Os últimos 3 meses completos
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        const threeMonthsAgoStart = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        endDate = lastMonthEnd;
        startDate = threeMonthsAgoStart;
        break;
      case "custom":
        // Personalizado: Pode incluir hoje
        if (customStartDate && customEndDate) {
          return { startDate: customStartDate, endDate: customEndDate };
        }
        // Fallback para Mês Atual
        endDate = yesterday;
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default:
        endDate = yesterday;
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    return { startDate, endDate };
  }, [selectedPeriod, customStartDate, customEndDate]);

  const { data: metrics, isLoading: isLoadingMetrics } = useMetrics({
    productId: selectedProduct?.id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const { data: timeSeries, isLoading: isLoadingTimeSeries } = useMetricsTimeSeries({
    productId: selectedProduct?.id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const handleCustomDateChange = (start: Date | undefined, end: Date | undefined) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <DashboardHeader />
      
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <ProductSelector />
          <PeriodFilter 
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onCustomDateChange={handleCustomDateChange}
          />
        </div>
        <span className="text-sm text-muted-foreground font-medium">
          {formatDateRange(dateRange.startDate, dateRange.endDate)}
        </span>
      </div>

      <div id="dashboard-content" className="space-y-6">

        {/* Metrics Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="MRR"
            value={isLoadingMetrics ? "..." : `R$ ${metrics?.mrr.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
            change={metrics?.mrrChange}
            changeLabel={t('vsPreviousPeriod')}
            icon={<DollarSign className="h-5 w-5" />}
            trend={metrics && metrics.mrrChange > 0 ? "up" : metrics && metrics.mrrChange < 0 ? "down" : "neutral"}
            variant="highlight"
            description={t('mrrDescription')}
          />
          <MetricCard
            title="ARR"
            value={isLoadingMetrics ? "..." : `R$ ${metrics?.arr.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
            change={metrics?.arrChange}
            changeLabel={t('vsPreviousPeriod')}
            icon={<TrendingUp className="h-5 w-5" />}
            trend={metrics && metrics.arrChange > 0 ? "up" : metrics && metrics.arrChange < 0 ? "down" : "neutral"}
            description={t('arrDescription')}
          />
          <MetricCard
            title="Taxa de Churn"
            value={isLoadingMetrics ? "..." : `${metrics?.churn.toFixed(1)}%`}
            change={metrics?.churnChange}
            changeLabel={t('vsPreviousPeriod')}
            icon={<Users className="h-5 w-5" />}
            trend={metrics && metrics.churnChange < 0 ? "up" : metrics && metrics.churnChange > 0 ? "down" : "neutral"}
            description={t('churnDescription')}
          />
          <MetricCard
            title="Receita Total"
            value={isLoadingMetrics ? "..." : `R$ ${metrics?.totalRevenue.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
            change={metrics?.revenueChange}
            changeLabel={t('vsPreviousPeriod')}
            icon={<ShoppingCart className="h-5 w-5" />}
            trend={metrics && metrics.revenueChange > 0 ? "up" : metrics && metrics.revenueChange < 0 ? "down" : "neutral"}
            description={t('totalRevenueDescription')}
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid gap-4 md:grid-cols-3">
          <MetricCard
            title="CAC"
            value={isLoadingMetrics ? "..." : `R$ ${metrics?.cac.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
            change={metrics?.cacChange}
            changeLabel={t('vsPreviousPeriod')}
            icon={<Target className="h-5 w-5" />}
            trend={metrics && metrics.cacChange < 0 ? "up" : metrics && metrics.cacChange > 0 ? "down" : "neutral"}
            description={t('cacDescription')}
          />
          <MetricCard
            title="LTV"
            value={isLoadingMetrics ? "..." : `R$ ${metrics?.ltv.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
            change={metrics?.ltvChange}
            changeLabel={t('vsPreviousPeriod')}
            icon={<Calendar className="h-5 w-5" />}
            trend={metrics && metrics.ltvChange > 0 ? "up" : metrics && metrics.ltvChange < 0 ? "down" : "neutral"}
            description={t('ltvDescription')}
          />
          <MetricCard
            title="Ticket Médio"
            value={isLoadingMetrics ? "..." : `R$ ${metrics?.averageTicket.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}
            change={metrics?.averageTicketChange}
            changeLabel={t('vsPreviousPeriod')}
            icon={<DollarSign className="h-5 w-5" />}
            trend={metrics && metrics.averageTicketChange > 0 ? "up" : metrics && metrics.averageTicketChange < 0 ? "down" : "neutral"}
            description={t('averageTicketDescription')}
          />
        </div>

        {/* Charts */}
        <div className="grid gap-4 lg:grid-cols-2">
          <RevenueChart data={isLoadingTimeSeries ? [] : timeSeries?.revenueData} />
          <ChurnChart data={isLoadingTimeSeries ? [] : timeSeries?.churnData} />
        </div>

        {/* Info Card */}
        <div className="rounded-lg border border-primary/20 bg-primary-light p-6">
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary p-3">
              <TrendingUp className="h-6 w-6 text-primary-foreground" />
            </div>
            <div className="space-y-1">
              <h3 className="font-semibold text-primary">{t('connectPaymentGateways')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('integrateWithStripe')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
